<template>
  <div class="tag-list-container" :class="{'is-blocked-tags': isBlockedTags}">
    <div v-if="isBlockedTags">
      <p class="p-mt-0 p-mb-6">Select Tags to be accessible for this team member. Remaining Tags and associated tagged content will be hidden.</p>
    </div>

    <template v-if="allowList">
      <div class="p-d-flex p-gap-6 p-jc-start">
        <div 
          v-for="(tagCategory, i) in tagCategories" 
          :key="i" 
          :id="`tag_category_${tagCategory.id}_container`"  
          class="p-mr-6"
          >
          <h3>{{tagCategory.name}}</h3>
          <div v-if="allowSelectAll" class="p-d-flex p-ai-center p-mb-4">
            <Checkbox
              v-if="allowSelect"
              :id="`tag_select_all_${tagCategory.id}`"
              class="tag-select-all p-mr-3"
              :value="tagCategory.id"
              v-model="selectedTagCategoryIntIds"
              @change="onSelectAllChange(tagCategory)"
            />
            <label :for="`tag_select_all_${tagCategory.id}`">{{ tagCategory.tags.every((tag) => localSelectedTagIntIds.includes(tag.id.intID)) ? 'Deselect' : 'Select' }} All</label>
          </div>
          <div
            v-for="(tag) of tagCategory.tags"
            :key="'tag_' + tag.id.intID"
          >
            <template v-if="overridePlan || (!overridePlan && ((isLeadPlan && !tag.isNested) || !isLeadPlan))">
              <div class="p-mb-2 p-d-flex p-ai-center">
                <template v-if="allowSelect">
                  <TriStateCheckbox 
                    v-if="isBlockedTags"
                    :id="'tag_' + tag.id.intID"
                    v-model="localSelectedTriStateIntIds[tag.id.intID]"
                    @change="onSelectTagChange(tag, tagCategory)"
                    class="p-mr-3"
                  >
                    <checkicon>T</checkicon>
                    <uncheckicon>F</uncheckicon>
                    <nullableicon>N</nullableicon>
                  </TriStateCheckbox>
                  <Checkbox
                    v-else
                    :id="'tag_' + tag.id.intID"
                    :value="tag.id.intID"
                    v-model="localSelectedTagIntIds"
                    @change="onSelectTagChange(tag, tagCategory)"
                    class="p-mr-3"
                  />
                </template>
                <label
                  :for="'tag_' + tag.id.intID"
                  :class="{'is-nested': tag.isNested, 'is-lead': tag.isLead}"
                >
                  <template v-if="(isLeadPlan && tag.isNested) || (!isLeadPlan && overridePlan && tag.isNested) || (isBlockedTags && tag.isNested)">
                    {{ tag.abbreviatedPlanName }} > {{ tag.title }}
                  </template>
                  <template v-else>
                    {{ tag.title }}
                  </template>
                </label>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
    <div v-if="allowCreate">
      <h3 v-if="allowList">Create New Tag</h3>
      <div class="p-field">
        <label for="newTagType">Type</label>
        <SelectButton
          id="newTagType"
          v-model="selectedTypeOption"
          :options="typeOptions"
          :multiple="false"
          class="p-mb-4"
        />
      </div>
      <div class="p-d-flex p-jc-between p-ai-center">
        <div class="p-field p-mr-4">
          <label for="newTag">Name</label>
          <InputText
            id="newTag"
            type="text"
            v-model="newTag.text"
            autocomplete="off"
          />
        </div>
        <Button @click="createTag">Add Tag</Button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Checkbox from 'primevue/checkbox'
import TriStateCheckbox from 'primevue/tristatecheckbox'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import SelectButton from 'primevue/selectbutton'
import Tag, {TagType} from '@/models/Tag'
import ID from '@/models/ID'
import Plan from '@/models/Plan'
import TagCategory from '@/models/TagCategory'

Vue.component('Checkbox', Checkbox)
Vue.component('TriStateCheckbox', TriStateCheckbox)
Vue.component('InputText', InputText)
Vue.component('Button', Button)
Vue.component('SelectButton', SelectButton)

export default Vue.extend({
  name: 'TagList',
  props: {
    allowSelect: Boolean,
    allowEdit: Boolean,
    allowCreate: Boolean,
    allowList: Boolean,
    selectedTags: Array as () => Tag[],
    isBlockedTags: Boolean,
    skipRefetch: Boolean,
    overridePlan: Object,
    allowSelectAll: {
      type: Boolean,
      default: false,
    }
  },
  data: () => {
    return {
      newTag: {} as Tag,
      currentEditingTags: [] as Tag[],
      localSelectedTagIntIds: [] as number[],
      localSelectedTriStateIntIds: {},
      typeOptions: [
        TagType.Objective,
        TagType.TargetSegment,
        TagType.JourneyPhase,
        TagType.Custom,
      ] as string[],
      selectedTypeOption: TagType.Objective as string,
      selectedTagCategoryIntIds: [] as number[],
    }
  },
  computed: {
    isLeadPlan(): boolean {
      return this.currentPlan.isLead
    },
    isNestedPlan(): boolean {
      return this.currentPlan.isNested
    },
    currentPlan(): Plan {
      return this.overridePlan ? this.overridePlan : this.$store.getters.currentPlan
    },
    tags(): Tag[] {
      // Filter out non-nested tags, if needed
      // if(this.isNestedPlan){
      //   return this.currentPlan.tags.filter((tag) => tag.isNested === true)
      // }
      return this.currentPlan.tags
    },
    tagCategories(): TagCategory[] {
      if(this.isNestedPlan){
        return this.currentPlan.tagCategories
          // Filter out categories with no nested tags, if needed
          // .filter((tagCategory) => tagCategory.tags.filter((tag) => tag.isNested === true).length)
          .map((tagCategory) => {
            return {
              ...tagCategory,
              tags: tagCategory.tags
                      // Filter out non-nested tags, if needed
                      // .filter((tag) => tag.isNested === true)
                      .map((tag) => {
                        // Ensure that abbreviated name is populated (somehow it's not populated in AppStore)
                        tag.abbreviatedPlanName = this.currentPlan.abbreviatedName
                        return tag
                      })
            }
          })
      }
      return this.currentPlan.tagCategories
    },
  },
  watch: {
    localSelectedTagIntIds() {
      this.$emit('updateSelection', [
        ...this.tags.filter(
          (tag) => this.localSelectedTagIntIds?.indexOf(tag.id.intID) > -1
        ),
      ])
    },
    'localSelectedTriStateIntIds': {
      deep: true,
      handler() {
        this.tags.forEach((tag) => {
          if(this.localSelectedTriStateIntIds[tag.id.intID] === false){
            // Blocked - add to localSelectedTagIntIds
            this.localSelectedTagIntIds.push(tag.id.intID)
          }else{
            // Visible or Null - remove from localSelectedTagIntIds
            this.localSelectedTagIntIds = this.localSelectedTagIntIds.filter((intId) => intId !== tag.id.intID)
          }
        })
      },
    },
  },
  mounted: function () {
    if (this.allowCreate) {
      this.newTag = new Tag()
    }
    if (this.allowSelect) {
      this.localSelectedTagIntIds = [
        ...this.selectedTags.map((tag) => tag.id.intID),
      ]

      // Set up object to manage tri-state checkboxes for blocked tags
      if(this.isBlockedTags){
        const newTriStateObject = {}
        this.tags.forEach((tag) => {
          newTriStateObject[tag.id.intID] = null
          if(this.tagCategoryHasSelectionsForTag(tag)){
            // Visible tag
            newTriStateObject[tag.id.intID] = true
          }
          if(this.localSelectedTagIntIds.includes(tag.id.intID)){
            // Blocked tag
            newTriStateObject[tag.id.intID] = false
          }
        })
        this.localSelectedTriStateIntIds = newTriStateObject
      }
    }
  },
  methods: {
    tagCategoryHasSelectionsForTag(inputTag: Tag) {
      const tagCategoryForTag = this.tagCategories.find((tagCategory) => {
        return tagCategory.tags.map((tag) => tag.id.intID).includes(inputTag.id.intID)
      })
      return this.localSelectedTagIntIds.filter((tagIntId) => tagCategoryForTag?.tags.map((tag) => tag.id.intID).includes(tagIntId)).length > 0
    },
    onSelectAllChange(inputTagCategory: TagCategory) {
      const inputTagIntIds = inputTagCategory.tags.map((tag) => tag.id.intID)
      if(inputTagCategory.tags.every(tag => this.localSelectedTagIntIds.includes(tag.id.intID))){
        // Remove all IDs
        this.localSelectedTagIntIds = this.localSelectedTagIntIds.filter(tagIntId => !inputTagIntIds.includes(tagIntId))
        this.selectedTagCategoryIntIds = this.selectedTagCategoryIntIds.filter(categoryId => categoryId !== inputTagCategory.id)
      }else{
        // Add all IDs
        inputTagIntIds.forEach((tagIntId) => {
          if (!this.localSelectedTagIntIds.includes(tagIntId)) {
            this.localSelectedTagIntIds.push(tagIntId)
          }
        })
        this.selectedTagCategoryIntIds.push(inputTagCategory.id)
      }      
    },
    onSelectTagChange(inputTag: Tag, inputTagCategory: TagCategory) {
      // Manage selected categories
      if(inputTagCategory.tags.every(tag => this.localSelectedTagIntIds.includes(tag.id.intID))){
        this.selectedTagCategoryIntIds.push(inputTagCategory.id)
      }else{
        this.selectedTagCategoryIntIds = this.selectedTagCategoryIntIds.filter(categoryId => categoryId !== inputTagCategory.id)
      }

      // Manage selections for blocked tags
      if(this.isBlockedTags){
        const visibleTagsInCategory = inputTagCategory.tags.filter((tag) => this.localSelectedTriStateIntIds[tag.id.intID] === true)
        const blockedTagsInCategory = inputTagCategory.tags.filter((tag) => this.localSelectedTriStateIntIds[tag.id.intID] === false)
        const nullTagsInCategory = inputTagCategory.tags.filter((tag) => this.localSelectedTriStateIntIds[tag.id.intID] === null)
        if((visibleTagsInCategory.length > 0 || blockedTagsInCategory.length > 0) && this.localSelectedTriStateIntIds[inputTag.id.intID] === null){
          // If there are blocked and visible tags in category and changed tag is null, set changed tag to true
          this.localSelectedTriStateIntIds[inputTag.id.intID] = true
          // If all other tags are visible, set all to null
          if(visibleTagsInCategory.length === inputTagCategory.tags.length - 1){
            inputTagCategory.tags.forEach((tag) => {
              this.localSelectedTriStateIntIds[tag.id.intID] = null
            })
          }
        }else if(visibleTagsInCategory.length === 1 && nullTagsInCategory.length === inputTagCategory.tags.length - 1){
          // If there is only one tag in the category visible, set others in category to blocked
          inputTagCategory.tags.forEach((tag) => {
            if(tag.id.intID !== inputTag.id.intID){
              this.localSelectedTriStateIntIds[tag.id.intID] = false
            }
          })
        }
      }
    },
    isEditingTag(tag: Tag): boolean {
      return this.currentEditingTags.find(
        (checkTag) => checkTag.id.intID == tag.id.intID
      )
        ? true
        : false
    },
    editTag(tag: Tag) {
      if (this.isEditingTag(tag)) {
        this.updateTag(tag)
      } else {
        this.currentEditingTags.push(tag)
      }
    },
    cancelEditTag(tag: Tag) {
      this.currentEditingTags.splice(this.currentEditingTags.indexOf(tag), 1)
    },
    createTag() {
      if (this.selectedTypeOption && this.selectedTypeOption !== 'Custom') {
        this.newTag.text = this.selectedTypeOption + ': ' + this.newTag.text
      }
      this.newTag.planId = new ID(
        this.currentPlan.id.intID,
        this.currentPlan.id.apiID
      )
      this.$store.getters.services.tags
        .create(this.newTag)
        .then((returnedTag) => {
          this.newTag = new Tag()

          if (this.skipRefetch) {
            this.$emit('addTag', returnedTag)
          } else {
            this.$store.dispatch('refreshCurrentPlan', {openedChannels: [], planToUse: this.currentPlan})
          }
        })
    },
    updateTag(tag: Tag) {
      this.$store.getters.services.tags.update(tag).then((returnedTag) => {
        this.cancelEditTag(tag)
      })
    },
    deleteTag(tag: Tag) {
      this.$store.getters.services.tags.delete([tag.id.intID]).then(() => {
        this.$store.dispatch('refreshCurrentPlan', {openedChannels: [], planToUse: this.currentPlan})
      })
    },
  },
})
</script>

<style lang="scss">
.tag-list-container {
  label.is-lead {
    display: flex;
    column-gap: 1rem;

    &::before {
      content: '\e925'; // pi-home
      display: block;
      font-family: 'primeicons';
      font-size: 1.6rem;
      font-style: normal;
      font-weight: normal;
      font-variant: normal;
      text-transform: none;
    }
  }

  &.is-blocked-tags {
    .p-checkbox {
      &:not(.tag-select-all) {
        .p-checkbox-box:has(.pi-times) {
          background-color: #000;
        }
        .p-checkbox-icon.pi-times {
          &::before {
            content: "\e965";
            color: #fff;
          }
        }
      }
    }
  }

}
</style>
